import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Select, Space, Typography, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useBillFormats, useCreateEligibilityCheck } from "eligibility/queries";
import { EligibilityResult } from "eligibility/components/EligibilityResult";
import { StatementTable } from "eligibility/components/StatementTable";
import { SaveToProject } from "eligibility/components/SaveToProject";
import { downloadEligibilityCsv } from "eligibility/utils/exportEligibilityCsv";
import { KW_LIMIT, THERM_LIMIT } from "eligibility/utils/verdict";
import { IEligibilityCheck } from "eligibility/models";

export const EligibilityChecker = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: formats } = useBillFormats(organizationId);
  const createCheck = useCreateEligibilityCheck(organizationId);

  const [billFormat, setBillFormat] = useState<string>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [check, setCheck] = useState<IEligibilityCheck>();

  const onSubmit = () => {
    if (!billFormat) {
      message.warning("Select a bill format first.");
      return;
    }
    const files = fileList
      .map((f) => f.originFileObj as File)
      .filter(Boolean);
    if (!files.length) {
      message.warning("Upload at least one bill.");
      return;
    }
    createCheck.mutate(
      { billFormat, files },
      {
        onSuccess: setCheck,
        onError: () => message.error("Could not screen these bills."),
      }
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1080, margin: "0 auto" }}>
      <Typography.Title level={3}>
        NJ Direct Install — Eligibility Checker
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Qualifies if 12-mo avg demand &lt; {KW_LIMIT} kW OR annual usage &lt;{" "}
        {THERM_LIMIT.toLocaleString()} therms.
      </Typography.Paragraph>

      <Space wrap align="end" style={{ marginBottom: 20 }}>
        <div>
          <div>Utility / bill format</div>
          <Select
            style={{ width: 340 }}
            placeholder="Select a bill format"
            aria-label="Bill format"
            value={billFormat}
            onChange={setBillFormat}
            options={formats?.map((f) => ({ value: f.id, label: f.name }))}
          />
        </div>
        <Upload
          multiple
          accept=".pdf,image/*"
          beforeUpload={() => false}
          fileList={fileList}
          onChange={({ fileList: fl }) => setFileList(fl)}
        >
          <Button icon={<UploadOutlined />}>Upload one site&apos;s bills</Button>
        </Upload>
        <Button
          type="primary"
          loading={createCheck.isPending}
          onClick={onSubmit}
        >
          Screen bills
        </Button>
      </Space>

      {check && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <EligibilityResult check={check} />
          <StatementTable statements={check.statements} />
          <Space>
            <Button
              onClick={() => {
                setCheck(undefined);
                setFileList([]);
              }}
            >
              Clear
            </Button>
            <Button onClick={() => downloadEligibilityCsv(check)}>
              Export CSV
            </Button>
            <SaveToProject checkId={check.id} organizationId={organizationId} />
          </Space>
        </Space>
      )}
    </div>
  );
};

export default EligibilityChecker;
